// src/views/kiddo-games/drag-letters/components/DraggableLetter.tsx
// Kopia src/views/drag-letters/components/DraggableLetter.tsx (oryginał z Regular usunięty) — tylko dla Ułóż słowa Easy
import React, { memo, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import classes from '../KiddoDragLettersView.module.less';

gsap.registerPlugin(Draggable);

interface DraggableLetterProps {
  id: string;
  letter: string;
  placedInSlot: number | null;
  dropZonesRef: React.RefObject<(HTMLDivElement | null)[]>;
  onDrop: (letterId: string, targetSlot: number | null) => void;
}

export const DraggableLetter = memo(({
  id,
  letter,
  placedInSlot,
  dropZonesRef,
  onDrop,
}: DraggableLetterProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const draggableRef = useRef<Draggable | null>(null);
  const previousSlotRef = useRef<number | null>(placedInSlot);

  // Funkcja pomocnicza do animacji do określonego slotu
  const animateToSlot = (slotIndex: number) => {
    const element = elementRef.current;
    const targetZone = dropZonesRef.current?.[slotIndex];

    if (!element || !targetZone) return;

    const zoneRect = targetZone.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Oblicz docelową pozycję względem aktualnej pozycji elementu
    const targetX = zoneRect.left - elementRect.left + (zoneRect.width - elementRect.width) / 2;
    const targetY = zoneRect.top - elementRect.top + (zoneRect.height - elementRect.height) / 2;

    // Pobierz aktualne transformacje GSAP
    const currentX = gsap.getProperty(element, 'x') as number || 0;
    const currentY = gsap.getProperty(element, 'y') as number || 0;

    // Animuj do nowej pozycji
    gsap.to(element, {
      x: currentX + targetX,
      y: currentY + targetY,
      duration: 0.4,
      ease: 'power2.inOut',
    });
  };

  // Inicjalizacja Draggable - tylko RAZ na początku
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    // Kill poprzedni draggable
    if (draggableRef.current) {
      draggableRef.current.kill();
    }

    const playingStage = element.closest(`.${classes.playingStage}`);

    // Stwórz nowy Draggable
    [draggableRef.current] = Draggable.create(element, {
      type: 'x,y',
      edgeResistance: 0.65,
      // bounds: document.body,
      bounds: playingStage || window,
      // lub:
      // bounds: { minX: 0, maxX: window.innerWidth, minY: 0, maxY: window.innerHeight },
      inertia: true,
      autoScroll: 1,

      allowContextMenu: true,
      force3D: true,

      onPress() {
        // Podnieś element podczas chwytania
        gsap.set(element, { zIndex: 1000, scale: 1.1 });
      },

      onRelease() {
        // Przywróć skalę
        gsap.set(element, { scale: 1 });
      },

      onDragEnd() {
        gsap.set(element, { zIndex: 'auto' });

        const draggedRect = element.getBoundingClientRect();
        const draggedCenterX = draggedRect.left + draggedRect.width / 2;
        const draggedCenterY = draggedRect.top + draggedRect.height / 2;

        let closestSlotIndex: number | null = null;
        let closestDistance = Infinity;

        // Sprawdź wszystkie sloty
        if (dropZonesRef.current) {
          dropZonesRef.current.forEach((zone, index) => {
            if (!zone) return;

            const zoneRect = zone.getBoundingClientRect();
            const zoneCenterX = zoneRect.left + zoneRect.width / 2;
            const zoneCenterY = zoneRect.top + zoneRect.height / 2;

            const distance = Math.sqrt(
              (draggedCenterX - zoneCenterX) ** 2
              + (draggedCenterY - zoneCenterY) ** 2,
            );

            if (distance < 100 && distance < closestDistance) {
              closestDistance = distance;
              closestSlotIndex = index;
            }
          });
        }

        // Jeśli znaleziono slot, animuj do niego
        if (closestSlotIndex !== null) {
          const targetZone = dropZonesRef.current?.[closestSlotIndex];
          if (targetZone) {
            const zoneRect = targetZone.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // Oblicz docelową pozycję
            const targetX = zoneRect.left - elementRect.left + (zoneRect.width - elementRect.width) / 2;
            const targetY = zoneRect.top - elementRect.top + (zoneRect.height - elementRect.height) / 2;

            // Animuj do slotu
            gsap.to(element, {
              x: `+=${targetX}`,
              y: `+=${targetY}`,
              duration: 0.3,
              ease: 'power2.inOut',
              onComplete: () => {
                onDrop(id, closestSlotIndex);
              },
            });
          }
        } else {
          // Jeśli nie trafił w slot, może wrócić do puli
          onDrop(id, null);
        }
      },
    });

    // Cleanup
    return () => {
      if (draggableRef.current) {
        draggableRef.current.kill();
      }
    };
  }, [id, onDrop, dropZonesRef]);

  // Efekt dla zmiany placedInSlot - obsłuż zarówno powrót do puli jak i zamianę miejsc
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !dropZonesRef.current) return;

    // Sprawdź czy to nie jest początkowa wartość
    if (previousSlotRef.current === placedInSlot) return;

    // Jeśli litera została wyparta przez inną (wraca do puli)
    if (placedInSlot === null) {
      // Wróć do pozycji początkowej z animacją
      gsap.to(element, {
        x: 0,
        y: 0,
        duration: 0.4,
        ease: 'back.out(1.2)',
      });
    } else if (previousSlotRef.current !== null && placedInSlot !== previousSlotRef.current) {
      // Animuj do nowego slotu
      animateToSlot(placedInSlot);
    }

    // Zaktualizuj poprzedni slot
    previousSlotRef.current = placedInSlot;
  }, [placedInSlot, dropZonesRef]);

  // Po zmianie rozmiaru gry (szerokość okna, obrót telefonu) pola i pula układają się na nowo, a przesunięcie kafelka
  // zostaje stare. Kafelek leżący w polu wraca więc od razu, bez animacji, na środek swojego pola
  useEffect(() => {
    const element = elementRef.current;
    const playingStage = element?.closest(`.${classes.playingStage}`);
    const slotIndex = placedInSlot;

    if (!element || !playingStage || slotIndex === null) return undefined;

    const observer = new ResizeObserver(() => {
      const targetZone = dropZonesRef.current?.[slotIndex];

      // Kafelka w ruchu (przeciąganego albo w trakcie animacji) nie ruszamy
      if (!targetZone || draggableRef.current?.isDragging || gsap.isTweening(element)) return;

      const zoneRect = targetZone.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const targetX = zoneRect.left - elementRect.left + (zoneRect.width - elementRect.width) / 2;
      const targetY = zoneRect.top - elementRect.top + (zoneRect.height - elementRect.height) / 2;

      gsap.set(element, { x: `+=${targetX}`, y: `+=${targetY}` });
    });

    observer.observe(playingStage);

    return () => observer.disconnect();
  }, [placedInSlot, dropZonesRef]);

  return (
    <div
      ref={elementRef}
      id={id}
      className={classes.draggableLetter}
      style={{
        touchAction: 'none',
        cursor: 'grab',
      }}
    >
      {letter}
    </div>
  );
});
